import { FormEvent, useState } from 'react';
import { ApiError, DocumentState, NewListing, getDocument, upsert } from '../api';
import CodeSamples, { ingestSnippet } from '../components/CodeSamples';
import { examplesFor } from '../markets';
import { Session } from '../session';

/**
 * Ingest, in the browser and in four languages side by side.
 *
 * The address is required, and it is required for a reason worth showing rather
 * than asserting: a listing with an address goes through geo-service's address
 * ladder on ingest, comes back with a coordinate, a metro and location tags, and
 * becomes findable by area and commute. Without one it still indexes and still
 * matches on text — so the page reports what the document actually resolved to
 * afterwards, instead of claiming success and leaving the difference invisible.
 */

interface FormState {
  doc_id: string;
  title: string;
  body: string;
  price: string;
  currency: string;
  bedrooms: string;
  bathrooms: string;
  street: string;
  suburb: string;
  city: string;
  amenities: string;
}

function blankForm(currency: string): FormState {
  return {
    doc_id: suggestedId(),
    title: '',
    body: '',
    price: '',
    // The tenant's own currency, not a hard-coded 'USD'. A South African
    // listing submitted in dollars is indexed at roughly a twentieth of its
    // real price and quietly ruins every price filter it takes part in.
    currency,
    bedrooms: '2',
    bathrooms: '1',
    street: '',
    suburb: '',
    city: '',
    amenities: '',
  };
}

function suggestedId(): string {
  // Human-readable and unique enough for a demo. doc_id is the tenant's own id
  // and is what upsert keys on, so reusing one is an update, not a duplicate.
  return `demo-web-${Date.now().toString(36)}`;
}

function toListing(form: FormState): NewListing {
  const number = (value: string) => {
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  };

  return {
    doc_id: form.doc_id.trim() || suggestedId(),
    title: form.title.trim(),
    ...(form.body.trim() ? { body: form.body.trim() } : {}),
    ...(number(form.price) != null ? { price: number(form.price), currency: form.currency } : {}),
    ...(number(form.bedrooms) != null ? { bedrooms: number(form.bedrooms) } : {}),
    ...(number(form.bathrooms) != null ? { bathrooms: number(form.bathrooms) } : {}),
    ...(form.suburb.trim() ? { area_name: form.suburb.trim() } : {}),
    ...(form.city.trim() ? { city: form.city.trim() } : {}),
    ...(form.amenities.trim()
      ? { amenities: form.amenities.split(',').map((a) => a.trim()).filter(Boolean) }
      : {}),
    address: {
      ...(form.street.trim() ? { street: form.street.trim() } : {}),
      ...(form.suburb.trim() ? { suburb: form.suburb.trim() } : {}),
      ...(form.city.trim() ? { city: form.city.trim() } : {}),
    },
  };
}

export default function AddListing({ session }: { session: Session }) {
  // Placeholders, currency options and the default all come from the tenant's
  // market: a form suggesting "Harare" to a South African prospect gets the one
  // detail they are guaranteed to notice wrong.
  const examples = examplesFor(session.tenant.markets, session.tenant.currency);
  const [form, setForm] = useState<FormState>(() => blankForm(examples.currency));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<{ docId: string; status: string; reason?: string } | null>(null);
  const [state, setState] = useState<DocumentState | null>(null);
  const [polling, setPolling] = useState(false);

  const set = (field: keyof FormState) => (event: { target: { value: string } }) =>
    setForm((current) => ({ ...current, [field]: event.target.value }));

  const listing = toListing(form);
  // The address is the one hard requirement, and "an address" means at least a
  // suburb and a city — a street on its own is not resolvable.
  const addressComplete = Boolean(form.suburb.trim() && form.city.trim());
  const canSubmit = Boolean(form.title.trim()) && addressComplete && !busy;

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!canSubmit) return;

    setBusy(true);
    setError(null);
    setOutcome(null);
    setState(null);

    try {
      const response = await upsert(session.key, listing);
      const result = response.results?.[0];
      setOutcome({
        docId: listing.doc_id,
        status: result?.status ?? (response.accepted ? 'accepted' : 'unknown'),
        reason: result?.reason,
      });

      if (result?.status !== 'rejected') void follow(listing.doc_id);
    } catch (err) {
      setError(err instanceof ApiError ? err.friendly : 'Could not add the listing.');
    } finally {
      setBusy(false);
    }
  }

  /**
   * Watch the document until it is indexed.
   *
   * Ingest answers 202 because embedding is a network round trip to a GPU host
   * and no tenant's write path should block on it. That is the right design and
   * a confusing demo: the listing is accepted but not yet searchable. Polling
   * GET /v1/documents/{id} makes the wait visible and short, and shows the
   * geocoding result — which is the part worth watching.
   */
  async function follow(docId: string): Promise<void> {
    setPolling(true);
    const deadline = Date.now() + 90_000;

    while (Date.now() < deadline) {
      try {
        const current = await getDocument(session.key, docId);
        setState(current);
        if (current.index_state !== 'pending') break;
      } catch {
        // A document that is not readable yet is not an error worth showing;
        // the loop's own timeout is the backstop.
      }
      await new Promise((resolve) => setTimeout(resolve, 2000));
    }
    setPolling(false);
  }

  return (
    <main className="page">
      <h1>Add a listing</h1>
      <p className="lede">
        This writes to your demo index through the same ingest endpoint your feed loader would use.
        Submit it here, or copy the equivalent call below.
      </p>

      {/* Single column, code first.
          Side by side, the form column was ~280 px wide: every label wrapped
          onto three lines and the inputs were narrower than the values they
          hold. The code block is also the thing worth reading first — it is the
          pitch — so it leads, and the form it describes follows. */}
      <div className="stack">
        <div className="card">
          <h2>The same call, in your stack</h2>
          <p className="footnote" style={{ marginTop: 0, marginBottom: 12 }}>
            Updates live as you fill the form in. Send up to 500 documents per call.
          </p>
          <CodeSamples snippets={ingestSnippet(listing)} />
        </div>

        <form className="card" onSubmit={submit}>
          {error && <div className="note error">{error}</div>}

          <div className="field">
            <label htmlFor="title">
              Title<span className="req">*</span>
              <span className="hint">Leads the embedded document, so it carries most of the matching.</span>
            </label>
            <input id="title" value={form.title} onChange={set('title')} placeholder={examples.title} />
          </div>

          <div className="field">
            <label htmlFor="body">Description</label>
            <textarea id="body" value={form.body} onChange={set('body')} placeholder={examples.body} />
          </div>

          <h3 style={{ marginTop: 20 }}>Address (required)</h3>
          <p className="footnote" style={{ marginTop: -4, marginBottom: 12 }}>
            Resolved to a coordinate on ingest. Without it the listing is still indexed and still
            matches on text, but cannot be found by area or commute.
          </p>

          <div className="fields three">
            <div className="field">
              <label htmlFor="street">Street</label>
              <input id="street" value={form.street} onChange={set('street')} placeholder={examples.street} />
            </div>

            <div className="field">
              <label htmlFor="suburb">
                Suburb<span className="req">*</span>
              </label>
              <input id="suburb" value={form.suburb} onChange={set('suburb')} placeholder={examples.suburb} />
            </div>

            <div className="field">
              <label htmlFor="city">
                City<span className="req">*</span>
              </label>
              <input id="city" value={form.city} onChange={set('city')} placeholder={examples.city} />
            </div>
          </div>

          <h3 style={{ marginTop: 20 }}>Details</h3>

          <div className="fields four">
            <div className="field">
              <label htmlFor="price">Rent per month</label>
              <input id="price" inputMode="decimal" value={form.price} onChange={set('price')} placeholder={examples.price} />
            </div>
            <div className="field">
              <label htmlFor="currency">Currency</label>
              <select id="currency" value={form.currency} onChange={set('currency')}>
                {examples.currencies.map((code) => (
                  <option key={code} value={code}>
                    {code}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label htmlFor="bedrooms">Bedrooms</label>
              <input id="bedrooms" inputMode="numeric" value={form.bedrooms} onChange={set('bedrooms')} />
            </div>
            <div className="field">
              <label htmlFor="bathrooms">Bathrooms</label>
              <input id="bathrooms" inputMode="numeric" value={form.bathrooms} onChange={set('bathrooms')} />
            </div>
          </div>

          <div className="field">
            <label htmlFor="amenities">
              Amenities
              <span className="hint">Comma separated. These are embedded and matched, not just stored.</span>
            </label>
            <input id="amenities" value={form.amenities} onChange={set('amenities')} placeholder={examples.amenities} />
          </div>

          <div className="field">
            <label htmlFor="docid">
              Your document id
              <span className="hint">Your own id, opaque to us. Reusing one updates that listing rather than adding a second.</span>
            </label>
            <input id="docid" className="mono" value={form.doc_id} onChange={set('doc_id')} />
          </div>

          <button type="submit" disabled={!canSubmit}>
            {busy ? <><span className="spinner" /> Sending</> : 'Add to the index'}
          </button>
          {!addressComplete && (
            <p className="footnote">A suburb and a city are needed before this can be sent.</p>
          )}
        </form>

        {outcome && (
            <div className="card">
              <h3>Result</h3>
              {outcome.status === 'rejected' ? (
                <div className="note error" style={{ marginBottom: 0 }}>
                  Rejected: {outcome.reason ?? 'the service did not say why'}
                </div>
              ) : (
                <div className="note ok" style={{ marginBottom: 12 }}>
                  {outcome.status === 'unchanged'
                    ? 'Identical to what is already indexed — nothing to do.'
                    : 'Accepted for indexing (HTTP 202).'}
                </div>
              )}

              <div className="parsed">
                <div className="parsed-row">
                  <span className="k">doc_id</span>
                  <span className="v">{outcome.docId}</span>
                </div>
                {state && (
                  <>
                    <div className="parsed-row">
                      <span className="k">index state</span>
                      <span className="v">
                        {state.index_state}
                        {polling && state.index_state === 'pending' && <> <span className="spinner" /></>}
                      </span>
                    </div>
                    <div className="parsed-row">
                      <span className="k">embedding</span>
                      <span className="v">{state.has_embedding ? 'stored' : 'not yet'}</span>
                    </div>
                    <div className="parsed-row">
                      <span className="k">coordinate</span>
                      <span className="v">
                        {state.location?.lat != null
                          ? `${state.location.lat.toFixed(5)}, ${state.location.lon?.toFixed(5)}`
                          : 'not resolved'}
                      </span>
                    </div>
                    {state.location?.precision && (
                      <div className="parsed-row">
                        <span className="k">precision</span>
                        <span className="v">{state.location.precision} · {state.location.source}</span>
                      </div>
                    )}
                    {state.location?.metro && (
                      <div className="parsed-row">
                        <span className="k">metro</span>
                        <span className="v">{state.location.metro}</span>
                      </div>
                    )}
                    {state.index_error && (
                      <div className="parsed-row">
                        <span className="k">error</span>
                        <span className="v">{state.index_error}</span>
                      </div>
                    )}
                  </>
                )}
              </div>

              {state?.geo_tags?.length ? (
                <>
                  <h3 style={{ marginTop: 16 }}>Location tags derived on ingest</h3>
                  <div className="chips">
                    {state.geo_tags.map((tag) => (
                      <span className="chip" key={tag}>{tag}</span>
                    ))}
                  </div>
                </>
              ) : null}

              {state?.index_state === 'indexed' && (
                <p className="footnote">
                  It is now searchable. Try the Search tab with its suburb or a phrase from the
                  description.
                </p>
              )}
              {state?.location?.lat == null && state?.index_state === 'indexed' && (
                <div className="note warn" style={{ marginTop: 12, marginBottom: 0 }}>
                  The address did not resolve to a coordinate, so this listing has no area or
                  commute data. Usually the suburb is not one the gazetteer knows for this market.
                </div>
              )}
            </div>
          )}
      </div>
    </main>
  );
}
